import React, { useContext, useState } from "react";
import { ThemeContext } from "../context/ThemeContext";
import {
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  TextField,
  Button,
  Chip,
  Stack,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PaletteIcon from "@mui/icons-material/Palette";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ColorLensIcon from "@mui/icons-material/ColorLens";

const palettes = [
  {
    background: "#2e7d32",
    dashboardBg: "#b9f7b6ff",
    bgNav: "#66bb6a",
    colorIcon: "invert(1)",
    bgColor: "#e8f5e9",
    bg: "#a5d6a7cc",
    bgProfile: "linear-gradient(135deg, #4caf50, #81c784)",
    cardBg: "#9ddb55cc",
    text: "#111111",
    primary: "#43a047",
    secondary: "#f57c00",
    name: "Fresh Meadow",
  },
  {
    background: "#1565c0",
    dashboardBg: "#bbdefbcc",
    bgNav: "#42a5f5",
    colorIcon: "invert(1)",
    bgColor: "#e3f2fd",
    bg: "#90caf9cc",
    bgProfile: "linear-gradient(135deg, #42a5f5, #1e88e5)",
    cardBg: "#ffffffcc",
    text: "#111111",
    primary: "#1976d2",
    secondary: "#00acc1",
    name: "Sky Blue",
  },
  {
    background: "#c62828",
    dashboardBg: "#ffcdd2ff",
    bgNav: "#ef5350",
    colorIcon: "invert(1)",
    bgColor: "#ffebee",
    bg: "#ef9a9acc",
    bgProfile: "linear-gradient(135deg, #ef5350, #e53935)",
    cardBg: "#ffffffcc",
    text: "#111111",
    primary: "#d32f2f",
    secondary: "#ff6f00",
    name: "Cherry Red",
  },
  {
    background: "#6a1b9a",
    dashboardBg: "#e1bee7ff",
    bgNav: "#ba68c8",
    colorIcon: "invert(1)",
    bgColor: "#f3e5f5",
    bg: "#ce93d8cc",
    bgProfile: "linear-gradient(135deg, #ab47bc, #8e24aa)",
    cardBg: "#ffffffcc",
    text: "#111111",
    primary: "#8e24aa",
    secondary: "#5e35b1",
    name: "Royal Purple",
  },
  {
    background: "#00838f",
    dashboardBg: "#b2ebf2ff",
    bgNav: "#26c6da",
    colorIcon: "invert(1)",
    bgColor: "#e0f7fa",
    bg: "#80deeacc",
    bgProfile: "linear-gradient(135deg, #26c6da, #00acc1)",
    cardBg: "#ffffffcc",
    text: "#111111",
    primary: "#00acc1",
    secondary: "#0097a7",
    name: "Tropical Cyan",
  },
  {
    background: "#d81b60",
    dashboardBg: "#f8bbd0ff",
    bgNav: "#ec407a",
    colorIcon: "invert(1)",
    bgColor: "#fce4ec",
    bg: "#f48fb1cc",
    bgProfile: "linear-gradient(135deg, #ec407a, #d81b60)",
    cardBg: "#ffffffcc",
    text: "#111111",
    primary: "#c2185b",
    secondary: "#ad1457",
    name: "Magenta Burst",
  },
  {
    background: "#ef6c00",
    dashboardBg: "#ffe0b2ff",
    bgNav: "#ffa726",
    colorIcon: "invert(1)",
    bgColor: "#fff3e0",
    bg: "#ffcc80cc",
    bgProfile: "linear-gradient(135deg, #ffa726, #fb8c00)",
    cardBg: "#ffffffcc",
    text: "#111111",
    primary: "#f57c00",
    secondary: "#ff6f00",
    name: "Sunset Orange",
  },
  {
    background: "#283593",
    dashboardBg: "#c5cae9ff",
    bgNav: "#5c6bc0",
    colorIcon: "invert(1)",
    bgColor: "#e8eaf6",
    bg: "#9fa8dacc",
    bgProfile: "linear-gradient(135deg, #5c6bc0, #3949ab)",
    cardBg: "#ffffffcc",
    text: "#111111",
    primary: "#303f9f",
    secondary: "#1a237e",
    name: "Deep Indigo",
  },
  {
    background: "#f9a825",
    dashboardBg: "#fff9c4ff",
    bgNav: "#ffeb3b",
    colorIcon: "invert(0)",
    bgColor: "#fffde7",
    bg: "#fff59dcc",
    bgProfile: "linear-gradient(135deg, #ffeb3b, #fdd835)",
    cardBg: "#ffffffcc",
    text: "#111111",
    primary: "#fbc02d",
    secondary: "#f57f17",
    name: "Golden Sun",
  },
  {
    background: "#558b2f",
    dashboardBg: "#dcedc8ff",
    bgNav: "#9ccc65",
    colorIcon: "invert(1)",
    bgColor: "#f1f8e9",
    bg: "#c5e1a5cc",
    bgProfile: "linear-gradient(135deg, #9ccc65, #7cb342)",
    cardBg: "#ffffffcc",
    text: "#111111",
    primary: "#689f38",
    secondary: "#33691e",
    name: "Lime Garden",
  },
  {
    background: "#00695c",
    dashboardBg: "#b2dfdbff",
    bgNav: "#4db6ac",
    colorIcon: "invert(1)",
    bgColor: "#e0f2f1",
    bg: "#80cbc4cc",
    bgProfile: "linear-gradient(135deg, #4db6ac, #26a69a)",
    cardBg: "#ffffffcc",
    text: "#111111",
    primary: "#00897b",
    secondary: "#004d40",
    name: "Emerald Teal",
  },
  {
    background: "#bf360c",
    dashboardBg: "#ffccbcff",
    bgNav: "#ff7043",
    colorIcon: "invert(1)",
    bgColor: "#fbe9e7",
    bg: "#ffab91cc",
    bgProfile: "linear-gradient(135deg, #ff7043, #ff5722)",
    cardBg: "#ffffffcc",
    text: "#111111",
    primary: "#e64a19",
    secondary: "#d84315",
    name: "Burnt Sienna",
  },
  {
    background: "#4527a0",
    dashboardBg: "#d1c4e9ff",
    bgNav: "#7e57c2",
    colorIcon: "invert(1)",
    bgColor: "#ede7f6",
    bg: "#b39ddbcc",
    bgProfile: "linear-gradient(135deg, #7e57c2, #5e35b1)",
    cardBg: "#ffffffcc",
    text: "#111111",
    primary: "#5e35b1",
    secondary: "#311b92",
    name: "Violet Dream",
  },
  {
    background: "#01579b",
    dashboardBg: "#b3e5fcff",
    bgNav: "#29b6f6",
    colorIcon: "invert(1)",
    bgColor: "#e1f5fe",
    bg: "#81d4facc",
    bgProfile: "linear-gradient(135deg, #29b6f6, #039be5)",
    cardBg: "#ffffffcc",
    text: "#111111",
    primary: "#0288d1",
    secondary: "#01579b",
    name: "Ocean Blue",
  },
  {
    background: "#880e4f",
    dashboardBg: "#f8bbd0ff",
    bgNav: "#f06292",
    colorIcon: "invert(1)",
    bgColor: "#fce4ec",
    bg: "#f48fb1cc",
    bgProfile: "linear-gradient(135deg, #f06292, #ec407a)",
    cardBg: "#ffffffcc",
    text: "#111111",
    primary: "#c2185b",
    secondary: "#880e4f",
    name: "Berry Wine",
  },
  {
    background: "#827717",
    dashboardBg: "#f0f4c3ff",
    bgNav: "#d4e157",
    colorIcon: "invert(0)",
    bgColor: "#f9fbe7",
    bg: "#dce775cc",
    bgProfile: "linear-gradient(135deg, #d4e157, #cddc39)",
    cardBg: "#ffffffcc",
    text: "#111111",
    primary: "#c0ca33",
    secondary: "#afb42b",
    name: "Chartreuse",
  },
]; 

export default function CustomThemePicker({ onClose = () => {} }) {
  const { setCustomColors, setMode } = useContext(ThemeContext);
  const { mode, customColors } = useContext(ThemeContext);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(customColors?.name || null);
  const [customTheme, setCustomTheme] = useState({
    background: "#1e3a8a",
    dashboardBg: "#bbdefbcc",
    bgNav: "#64b5f6",
    colorIcon: "invert(1)",
    bgColor: "#e3f2fd",
    bg: "#bbdefbcc",
    bgProfile: "linear-gradient(135deg, #64b5f6, #2196f3)",
    cardBg: "#ffffffcc",
    text: "#111111",
    primary: "#1e88e5",
    secondary: "#00acc1",
    name: "Custom",
  });

  const handleSelectTheme = (palette) => {
    setCustomColors(palette);
    setMode("custom");
    setSelectedTheme(palette.name);
  };

  const handleColorChange = (key, value) => {
    setCustomTheme((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApplyCustomTheme = () => {
    setCustomColors(customTheme);
    setMode("custom");
    setSelectedTheme("Custom");
    setShowColorPicker(false);
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxHeight: "85vh",
        display: "flex",
        flexDirection: "column",
        borderRadius: "20px",
        boxShadow:
          mode === "dark"
            ? "0 20px 60px rgba(0, 0, 0, 0.5)"
            : "0 20px 60px rgba(30, 58, 138, 0.15)",
        border:
          mode === "dark"
            ? "1px solid rgba(255, 255, 255, 0.1)"
            : "1px solid rgba(30, 139, 175, 0.1)",
        background:
          mode === "dark"
            ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
            : mode === "custom"
            ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
            : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
        overflow: "hidden",
      }}
    >
      {/* Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          background:
            mode === "dark"
              ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
              : mode === "custom"
              ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
              : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
          p: 4,
          pb: 2,
          borderRadius: "20px 20px 0 0",
          boxShadow:
            mode === "dark"
              ? "0 4px 12px rgba(0, 0, 0, 0.3)"
              : "0 4px 12px rgba(30, 139, 175, 0.1)",
        }}
      >
        {/* Close Icon */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
          color: mode === "dark" ? "white" : mode === "custom" ? "#f5f5f5" || "#222222" : "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
backgroundColor:
              mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(30, 139, 175, 0.1)",
            "&:hover": {
              backgroundColor:
                mode === "dark"
                  ? "rgba(255, 255, 255, 0.2)"
                  : "rgba(30, 139, 175, 0.2)",
            
            },
           
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Modal Heading */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
                 background: mode === "dark" ? "transparent" : mode === "custom" ? "transparent" || "transparent" : "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
              boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
            }}
          >
            <PaletteIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              sx={{
                 background: mode === "dark" ? "white" : mode === "custom" ? "#f5f5f5" || "#222222" : "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                fontWeight: 700,
                letterSpacing: "-0.5px",
              }}
            >
              Theme Customizer
            </Typography>
            <Typography
              variant="body2"
              sx={{
              color: mode === "dark" ? "white" : mode === "custom" ? "#f5f5f5" || "#222222" : "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",

                fontSize: "0.875rem",
              }}
            >
              Personalize your workspace experience
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 3,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background:
              mode === "dark"
                ? "rgba(255, 255, 255, 0.3)"
                : "rgba(30, 139, 175, 0.3)",
            borderRadius: "4px",
          },
        }}
      >
        <Stack spacing={3}>
          {/* Custom Theme Creator Button */}
          <Button
            variant="contained"
            fullWidth
            startIcon={<ColorLensIcon />}
            onClick={() => setShowColorPicker(!showColorPicker)}
            sx={{
              py: 1.8,
              borderRadius: "12px",
           color: mode === "dark" ? "white" : mode === "custom" ? "#f5f5f5" || "#222222" : "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",

              background: showColorPicker
                ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                : "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
              boxShadow: showColorPicker
                ? "0 4px 14px rgba(16, 185, 129, 0.3)"
                : "0 4px 14px rgba(30, 139, 175, 0.3)",
              textTransform: "none",
              fontSize: "0.95rem",
              fontWeight: 600,
              transition: "all 0.3s ease",
              "&:hover": {
                background: showColorPicker
                  ? "linear-gradient(135deg, #10b981 0%, #047857 100%)"
                  : "linear-gradient(135deg, #1e3a8a 0%, #1a7a9a 100%)",
                boxShadow: showColorPicker
                  ? "0 6px 20px rgba(16, 185, 129, 0.4)"
                  : "0 6px 20px rgba(30, 139, 175, 0.4)",
                transform: "translateY(-2px)",
              },
              "&:active": {
                transform: "translateY(0px)",
              },
            }}
          >
            {showColorPicker ? "Hide Color Picker" : "Create Custom Theme"}
          </Button>

          {/* Custom Color Picker Panel */}
          <Fade in={showColorPicker} timeout={400}>
            <Box
              sx={{
                display: showColorPicker ? "block" : "none",
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "12px",
                  backgroundColor:
                    mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : mode === "custom"
                      ? customColors?.inputBg || "rgba(255, 255, 255, 0.8)"
                      : "rgba(255, 255, 255, 0.8)",
                  border: `1.5px solid ${
                    mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(30, 139, 175, 0.15)"
                  }`,
                  transition: "all 0.3s ease",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                  <AutoAwesomeIcon
                    sx={{
                      color: "#1e8bafff",
                      fontSize: 22,
                    }}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color:
                        mode === "dark"
                          ? "#ffffff"
                          : mode === "custom"
                          ? customColors?.text || "#111111"
                          : "#37474f",
                    }}
                  >
                    Design Your Colors
                  </Typography>
                </Stack>

                <Grid container spacing={2.5}>
                  {[
                    { key: "primary", label: "Primary", color: customTheme.primary },
                    {
                      key: "secondary",
                      label: "Secondary",
                      color: customTheme.secondary,
                    },
                    {
                      key: "background",
                      label: "Background",
                      color: customTheme.background,
                    },
                    { key: "bgNav", label: "Navigation", color: customTheme.bgNav },
                    {
                      key: "dashboardBg",
                      label: "Dashboard",
                      color: customTheme.dashboardBg.slice(0, 7),
                      slice: true,
                    },
                    { key: "text", label: "Text", color: customTheme.text },
                  ].map((item) => (
                    <Grid item xs={6} key={item.key}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mb: 1,
                            fontWeight: 600,
                            color:
                              mode === "dark"
                                ? "rgba(255, 255, 255, 0.6)"
                                : "rgba(0, 0, 0, 0.6)",
                            fontSize: "0.75rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {item.label}
                        </Typography>
                        <Box
                          sx={{
                            position: "relative",
                            borderRadius: "8px",
                            overflow: "hidden",
                            border: `1.5px solid ${
                              mode === "dark"
                                ? "rgba(255, 255, 255, 0.1)"
                                : "rgba(30, 139, 175, 0.15)"
                            }`,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              borderColor: "rgba(30, 139, 175, 0.4)",
                              transform: "translateY(-2px)",
                              boxShadow:
                                mode === "dark"
                                  ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                                  : "0 4px 12px rgba(30, 139, 175, 0.1)",
                            },
                          }}
                        >
                          <TextField
                            type="color"
                            value={item.color}
                            onChange={(e) =>
                              handleColorChange(
                                item.key,
                                item.slice ? e.target.value + "cc" : e.target.value
                              )
                            }
                            fullWidth
                            sx={{
                              "& input": {
                                height: 56,
                                cursor: "pointer",
                                border: "none",
                              },
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                "& fieldset": {
                                  border: "none",
                                },
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleApplyCustomTheme}
                  startIcon={<CheckCircleIcon />}
                  sx={{
                    mt: 3,
                    py: 1.5,
                   color: mode === "dark" ? "white" : mode === "custom" ? "#f5f5f5" || "#222222" : "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                    boxShadow: "0 4px 14px rgba(30, 139, 175, 0.3)",
                    textTransform: "none",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "linear-gradient(135deg, #1e3a8a 0%, #1a7a9a 100%)",
                      boxShadow: "0 6px 20px rgba(30, 139, 175, 0.4)",
                      transform: "translateY(-2px)",
                    },
                    "&:active": {
                      transform: "translateY(0px)",
                      boxShadow: "0 2px 8px rgba(30, 139, 175, 0.3)",
                    },
                  }}
                >
                  Apply Custom Theme
                </Button>
              </Paper>
            </Box>
          </Fade>

          {/* Preset Themes Section */}
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
              color: mode === "dark" ? "white" : mode === "custom" ? "#f5f5f5" || "#222222" : "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",

                  fontSize: "0.95rem",
                }}
              >
                Preset Themes
              </Typography>
              <Chip
                label={`${palettes.length} Available`}
                size="small"
                sx={{
                  height: 24,
                  background:
                    mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(30, 139, 175, 0.1)",
                color: mode === "dark" ? "white" : mode === "custom" ? "#f5f5f5" || "#222222" : "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",

                  fontWeight: 600,
                  fontSize: "0.7rem",
                  borderRadius: "6px",
                }}
              />
            </Stack>

            <Grid container spacing={2}>
              {palettes.map((palette) => (
                <Grid item xs={6} sm={4} md={3} key={palette.name}>
                  <Paper
                    onClick={() => handleSelectTheme(palette)}
                    elevation={0}
                    sx={{
                      cursor: "pointer",
                      p: 2,
                      borderRadius: "12px",
                      textAlign: "center",
                      transition: "all 0.3s ease",
                      border:
                        selectedTheme === palette.name
                          ? "2px solid #1e8bafff"
                          : `1.5px solid ${
                              mode === "dark"
                                ? "rgba(255, 255, 255, 0.1)"
                                : "rgba(30, 139, 175, 0.15)"
                            }`,
                      position: "relative",
                      backgroundColor:
                        mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : mode === "custom"
                          ? customColors?.inputBg || "rgba(255, 255, 255, 0.8)"
                          : "rgba(255, 255, 255, 0.8)",
                      "&:hover": {
                        backgroundColor:
                          mode === "dark"
                            ? "rgba(255, 255, 255, 0.08)"
                            : "rgba(255, 255, 255, 1)",
                        borderColor: "rgba(30, 139, 175, 0.4)",
                        transform: "translateY(-4px)",
                        boxShadow:
                          mode === "dark"
                            ? "0 8px 24px rgba(0, 0, 0, 0.3)"
                            : "0 8px 24px rgba(30, 139, 175, 0.2)",
                      },
                    }}
                  >
                    {selectedTheme === palette.name && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          background: "#1e8bafff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 1,
                          boxShadow: "0 2px 8px rgba(30, 139, 175, 0.4)",
                        }}
                      >
                        <CheckCircleIcon
                          sx={{
                            color: "#fff",
                            fontSize: 16,
                          }}
                        />
                      </Box>
                    )}
                    <Box
                      sx={{
                        width: "100%",
                        height: 70,
                        borderRadius: "8px",
                        mb: 1.5,
                        background: palette.bgProfile,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color:
                          mode === "dark"
                            ? "#ffffff"
                            : mode === "custom"
                            ? customColors?.text || "#111111"
                            : "#37474f",
                        fontSize: "0.8rem",
                        display: "block",
                      }}
                    >
                      {palette.name}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Stack>
      </Box>
      {/* Sticky Footer */}
<Box
  sx={{
    position: "sticky",
    bottom: 0,
    zIndex: 2,
    background:
      mode === "dark"
        ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
        : mode === "custom"
        ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
        : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
    p: 2,
    borderRadius: "0 0 20px 20px",
    boxShadow:
      mode === "dark"
        ? "0 -4px 12px rgba(0, 0, 0, 0.3)"
        : "0 -4px 12px rgba(30, 139, 175, 0.1)",
    borderTop:
      mode === "dark"
        ? "1px solid rgba(255, 255, 255, 0.1)"
        : "1px solid rgba(30, 139, 175, 0.1)",
  }}
>
 <Button
  variant="outlined"
  onClick={onClose}
  fullWidth
  sx={{
    // Transparent background for all modes
    background: "transparent",

    // Adaptive border color (light border ALWAYS)
    borderColor:
      mode === "dark"
        ? "rgba(255,255,255,0.3)"
        : "rgba(0,0,0,0.2)",

    borderWidth: "1.5px",
           color: mode === "dark" ? "white" : mode === "custom" ? "#f5f5f5" || "#222222" : "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",

    fontWeight: 600,
    fontSize: "0.95rem",
    textTransform: "none",
    py: 1.4,
    borderRadius: "12px",

    // Very subtle hover — same border color, slight background transparency
    "&:hover": {
      background:
        mode === "dark"
          ? "rgba(255,255,255,0.06)"
          : "rgba(0,0,0,0.04)",
      borderColor:
        mode === "dark"
          ? "rgba(255,255,255,0.5)"
          : "rgba(0,0,0,0.3)",
    },

    // Active (pressed)
    "&:active": {
      background:
        mode === "dark"
          ? "rgba(255,255,255,0.1)"
          : "rgba(0,0,0,0.06)",
    },
  }}
>
  Done
</Button>

</Box>
    </Box>
  );
}